<script setup lang="ts">
  import {
    Button,
    ButtonVariant,
    EmptyState,
    EmptyStateSurface,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import emptyState from '@tundr/theme-assets/illustrations/empty-state.png';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, nextTick } from 'vue';
  import { useFilters } from '../../composables';
  import type { TableEmptyStateProps } from './TableEmptyState.types';

  const props = withDefaults(defineProps<TableEmptyStateProps>(), {
    filtered: false,
    persistentFilters: false,
  });

  defineSlots<{
    illustration?: () => unknown;
    action?: () => unknown;
  }>();

  const { t } = useLocale('table');

  const currentFilterStore = useFilters(props.filterNamespace);

  const resolvedTitle = computed(() => {
    if (props.title) return props.title;
    return props.filtered ? t('table.no_results_found') : t('table.no_items');
  });

  const resolvedDescription = computed(() => {
    if (props.description) return props.description;
    if (props.filtered) return t('table.no_items_subtitle_filtered');
    return props.entityName
      ? t('table.no_items_subtitle', { entityName: props.entityName })
      : t('table.no_items_subtitle_no_entity');
  });

  const showClearFilters = computed(
    () => props.filtered && !!props.filterNamespace,
  );

  const onClearFilters = () => {
    currentFilterStore.clearAllFilters(props.persistentFilters);
    // Focus on the search input or fallback button after clearing filters
    void nextTick(() => {
      const searchInput = document.querySelector<HTMLInputElement>(
        `[data-test-id="search-${props.filterNamespace}"]`,
      );
      const fallback = document.querySelector<HTMLButtonElement>(
        '[data-testid="table-filters-trigger"]',
      );
      (searchInput ?? fallback)?.focus();
    });
  };
</script>

<template>
  <EmptyState
    :surface="EmptyStateSurface.NONE"
    :svg-image="svgImage ?? emptyState"
    :svg-width="svgWidth"
    :svg-height="svgHeight"
    :title="resolvedTitle"
    :description="resolvedDescription"
    :test-id="testId"
  >
    <template v-if="$slots.illustration" #illustration>
      <slot name="illustration" />
    </template>
    <template v-if="$slots.action || showClearFilters" #action>
      <slot name="action">
        <Button
          :variant="ButtonVariant.Secondary"
          :icon-name="TUNDR_ICONS.CLOSE"
          test-id="table-empty-state-clear-filters"
          @click="onClearFilters"
        >
          {{ t('filters.clear_filters') }}
        </Button>
      </slot>
    </template>
  </EmptyState>
</template>
